import { Close, Save } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGridPro as DataGrid, GridColDef } from '@mui/x-data-grid-pro';
import { Loader } from 'components';
import ButtonSwitcher from 'components/ButtonSwitcher';
import ToggleButton from 'components/ToggleButton';
import useWindowDimensions from 'hooks/useWindowDimensions';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  changeDisplayModeAction,
  injectSummaryMonthAction,
  injectSummaryQuarterAction,
  setSummaryColumnsMonths,
  setSummaryColumnsQuarters,
  toggleIsSummaryDeployedAction,
} from 'store/actions/catalogToOrderActions';
import { setSnackbarAction } from 'store/actions/snackbarActions';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import { RootState } from 'store/redux';
import { DisplaySummaryMode } from 'types/enums/DisplaySummaryMode';
import { WorkunitMonth } from '../../helpersOrderWorkunits';
import useOrderWorkunitApi from '../../services/useOrderWorkunitApi';
import useOrderWorkunitsService from '../../services/useOrderWorkunitsService';

import './SummaryDataGrid.scss';
import useSummaryDatagridHelper from './useSummaryDatagridHelper';

export type ComputedWorkunits = {
  date?: string;
  total: number;
  randomId: number;
  title: string;
};

export interface WorkunitComplexityMonths extends WorkunitMonth {
  [x: number]: number;
}

const SummaryDataGrid = () => {
  const [t] = useTranslation();
  const loadings = useSelector((state) => state.loadings);

  const { state, dispatch } = useAppState();
  const { getColumnsMonths, getQuarterColumns, getValuesByMonths } = useSummaryDatagridHelper();
  const { computeSummaryMonths, redirectToPreview, computeSummaryQuarters } = useOrderWorkunitsService();
  const { updateOrderStatus, bulkCreateOrderWorkunits, exportExcel, getCatalogComplexities, getTenantWorkloads } =
    useOrderWorkunitApi();
  const selectedOrderWorkunits = useSelector((state: RootState) => state.catalogToOrder.selectedWorkunits);
  const order = useSelector((state) => state.catalogToOrder.order);
  const sliceMonth = useSelector((state) => state.catalogToOrder.summaryHandler.monthSlice);
  const sliceQuarter = useSelector((state) => state.catalogToOrder.summaryHandler.quarterSlice);
  const selectedScope = useSelector((state) => state.catalogToOrder.selectedScope);
  const months = useSelector((state) => state.catalogToOrder.months);
  const isOpen = useSelector((state) => state.catalogToOrder.summaryHandler.isOpen);
  const displayMode = useSelector((state) => state.catalogToOrder.summary.displayMode);
  const columnsMonths = useSelector((state) => state.catalogToOrder.summary.columnsMonths);
  const columnsQuarter = useSelector((state) => state.catalogToOrder.summary.columnsQuarter);
  const buttonsLoading = useSelector((state) => state.loadings.summary);
  const valuesMonths = useSelector((state) => state.catalogToOrder.summary.valuesMonths);
  const valuesQuarters = useSelector((state) => state.catalogToOrder.summary.valuesQuarters);
  const isDisplayModeMonthly = useCallback(() => displayMode === DisplaySummaryMode.MONTHLY, [displayMode]);

  const [isReferencesModalOpen, setIsReferencesModalOpen] = useState(false);
  const [specRef, setSpecRef] = useState('');
  const [chronoRef, setChronoRef] = useState('');
  const [referencesError, setReferencesError] = useState('');

  useEffect(() => {
    if (order?.catalog_id) {
      getCatalogComplexities(order?.catalog_id);
      getTenantWorkloads(order);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.catalog_id, months.length]);

  useEffect(() => {
    getValuesByMonths();
  }, [getValuesByMonths]);

  useEffect(() => {
    if (order) {
      if (displayMode === DisplaySummaryMode.MONTHLY) {
        dispatch(injectSummaryMonthAction(computeSummaryMonths() ?? []));
      }
      if (displayMode === DisplaySummaryMode.QUARTERLY) {
        dispatch(injectSummaryQuarterAction(computeSummaryQuarters() ?? []));
      }
    }
    // setUsedQuartersWorkunits(() => computeSummaryMonths);
  }, [order, dispatch, computeSummaryMonths, computeSummaryQuarters, displayMode]);

  useEffect(() => {
    if (isDisplayModeMonthly()) {
      dispatch(setSummaryColumnsMonths(getColumnsMonths() as GridColDef[]));
    }
    if (!isDisplayModeMonthly()) {
      dispatch(setSummaryColumnsQuarters(getQuarterColumns() as GridColDef[]));
    }
  }, [
    sliceMonth,
    months,
    sliceQuarter,
    selectedScope,
    dispatch,
    getColumnsMonths,
    getQuarterColumns,
    isDisplayModeMonthly,
  ]);

  const handleValidationOrder = async (is_submitted: boolean, is_rogue = false) => {
    try {
      if (order) {
        if (!is_rogue) {
          if (is_submitted && order.status === 'drafted') {
            await updateOrderStatus(order);
          }
        }

        if (selectedOrderWorkunits.length) {
          await bulkCreateOrderWorkunits();
        }

        if (is_submitted && !is_rogue) {
          setIsExtractModalOpen(true);
        }
      }
    } catch (e) {
      const result = (e as Error).message;
      if (result) {
        dispatch(
          setSnackbarAction({
            message: `error while saving order with workunits: ${result}`,
            open: true,
            severity: 'error',
          })
        );
      } else {
        dispatch(
          setSnackbarAction({ message: `error while saving order with workunits :${e}`, open: true, severity: 'error' })
        );
      }
    }
  };

  const gridWrapperRef = React.useRef<HTMLDivElement>(null);
  React.useLayoutEffect(() => {
    if (!gridWrapperRef?.current) return;
    const gridDiv = gridWrapperRef?.current;
    if (!gridDiv) return;
    if (gridDiv) {
      const gridEl: HTMLDivElement = gridDiv.querySelector('div')!;
      if (!gridEl) return;
      gridEl.style.height = '';
    }
  });

  const dimensions = useWindowDimensions();

  const [isExtractModalOpen, setIsExtractModalOpen] = useState(false);
  const isExtractLoading = useSelector((state) => state.loadings.extract_excel_summary);
  const handleExtractModalClose = () => {
    setIsExtractModalOpen(false);
  };

  const handleOpenReferencesModal = () => {
    setIsExtractModalOpen(false);
    setIsReferencesModalOpen(true);
    setReferencesError('');
  };

  const handleReferencesModalClose = () => {
    setIsReferencesModalOpen(false);
    setSpecRef('');
    setChronoRef('');
    setReferencesError('');
  };

  const downloadExtractAndRedirectWithReferences = async () => {
    // Validate required fields
    if (!specRef.trim() || !chronoRef.trim()) {
      setReferencesError(t('Both reference fields are required'));
      return;
    }

    try {
      if (order) {
        await exportExcel(specRef.trim(), chronoRef.trim());

        setIsReferencesModalOpen(false);
        setSpecRef('');
        setChronoRef('');
        redirectToPreview(order?.status === 'pending');
      }
    } catch (error) {
      setReferencesError(t('Error occurred while extracting Excel file'));
      console.error('Extract error:', error);
    }
  };

  const downloadExtractAndRedirect = async () => {
    if (order) {
      await exportExcel(specRef.trim(), chronoRef.trim());
      redirectToPreview(order?.status === 'pending');
    }
  };

  return (
    <Grid container spacing={4} sx={{ flexWrap: { lg: 'nowrap' } }}>
      <Grid item xs={12} lg="auto" xl={2}>
        <ToggleButton
          isOpen={!isOpen}
          handleClick={() => dispatch(toggleIsSummaryDeployedAction())}
          sx={{ mb: 2 }}
          disabled={false}
        />
        <Typography color="neutral.main" variant="h2" sx={{ mb: 2 }}>
          {t('Quote_summary')}
        </Typography>
        <ButtonSwitcher size="small">
          <Button
            variant={displayMode === 'MONTHLY' ? 'contained' : 'text'}
            color={displayMode === 'MONTHLY' ? 'primary' : 'neutral'}
            onClick={() => dispatch(changeDisplayModeAction(DisplaySummaryMode.MONTHLY))}
          >
            {t('Monthly')}
          </Button>
          <Button
            variant={displayMode === 'QUARTERLY' ? 'contained' : 'text'}
            color={displayMode === 'QUARTERLY' ? 'primary' : 'neutral'}
            onClick={() => dispatch(changeDisplayModeAction(DisplaySummaryMode.QUARTERLY))}
          >
            {t('Quarterly')}
          </Button>
        </ButtonSwitcher>
      </Grid>
      {isDisplayModeMonthly() && (
        <Grid item xs={12} lg={8} ref={gridWrapperRef} sx={{ my: { lg: '-30px' } }}>
          <DataGrid
            rows={valuesMonths}
            columns={columnsMonths as GridColDef[]}
            checkboxSelection={false}
            disableSelectionOnClick
            disableColumnFilter
            disableColumnSelector
            disableColumnMenu
            disableColumnResize
            hideFooter
            autoHeight
            disableExtendRowFullWidth
            headerHeight={70}
            rowHeight={dimensions.currentHeight > 800 ? 37 : 26}
            getRowId={(row) => row.randomId}
            className="summary-datagrid"
          />
        </Grid>
      )}
      {!isDisplayModeMonthly() && (
        <Grid item xs={12} lg={8} ref={gridWrapperRef} sx={{ my: { lg: '-30px' } }}>
          <DataGrid
            rows={state.catalogToOrder.summary.valuesQuarters}
            columns={columnsQuarter as GridColDef[]}
            checkboxSelection={false}
            disableSelectionOnClick
            disableColumnFilter
            disableColumnSelector
            disableColumnMenu
            hideFooter
            disableExtendRowFullWidth
            autoHeight
            headerHeight={70}
            rowHeight={dimensions.currentHeight > 800 ? 45 : 32}
            pageSize={100}
            getRowId={(row) => row.randomId}
            className="summary-datagrid"
          />
        </Grid>
      )}
      {!buttonsLoading && (
        <Grid item xs={12} lg={2} alignSelf="center">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{ mb: 2 }}>
              <Tooltip className="tooltip_summary_download" title={t('TRIGGER_VALIDATION_SUMMARY') as string}>
                <LoadingButton
                  loading={!!loadings.api}
                  variant="contained"
                  color="success"
                  onClick={() => handleValidationOrder(true)}
                  disabled={!selectedOrderWorkunits || selectedOrderWorkunits.length === 0}
                >
                  {t('Validate_order')}
                </LoadingButton>
              </Tooltip>
            </Box>
            <Box>
              <LoadingButton
                loading={!!loadings.api}
                startIcon={<Save color="primary" />}
                onClick={() => handleValidationOrder(false)}
              >
                {t('Save')}
              </LoadingButton>
            </Box>
          </Box>
        </Grid>
      )}
      {buttonsLoading && (
        <Grid className=" buton_container ">
          <Loader size={40} className="loader_container" />
        </Grid>
      )}
      <Dialog
        open={isExtractModalOpen}
        onClose={handleExtractModalClose}
        id="excel-export-dialog"
        aria-labelledby="excel-export-dialog-title"
        aria-describedby="excel-export-dialog-description"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="excel-export-dialog-title">
          <Grid container flexDirection="row" justifyContent="space-between" alignItems="center">
            <h3>{t('Excel extraction')} </h3>{' '}
            <IconButton onClick={handleExtractModalClose} color="primary">
              <Close />
            </IconButton>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="excel-export-dialog-description">
            {t('Do you want to extract excel files ?')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => redirectToPreview(order?.status === 'pending')} variant="text">
            {t("Don't extract")}
          </Button>
          {!isExtractLoading && (
            <Button onClick={handleOpenReferencesModal} autoFocus variant="contained">
              {t('Extract')}
            </Button>
          )}
          {!!isExtractLoading && (
            <div className=" buton_container ">
              <Loader size={40} className="loader_container" />
            </div>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={isReferencesModalOpen}
        onClose={handleReferencesModalClose}
        id="references-dialog"
        aria-labelledby="references-dialog-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="references-dialog-title" sx={{ pb: 1 }}>
          <Grid container flexDirection="row" justifyContent="space-between" alignItems="center">
            <h3 style={{ margin: 0 }}>{t('Reference Details')}</h3>
            <IconButton onClick={handleReferencesModalClose} color="primary">
              <Close />
            </IconButton>
          </Grid>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ minWidth: '120px' }}>
                {t('Spec Ref')}{' '}
                <Typography display="inline" color="error">
                  *
                </Typography>
              </Typography>
              <TextField
                id="client_spec_ref"
                autoFocus
                size="small"
                variant="outlined"
                sx={{ width: '200px' }}
                value={specRef}
                onChange={(e) => setSpecRef(e.target.value)}
                error={!!referencesError && !specRef.trim()}
                required
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ minWidth: '120px' }}>
                {t('Chrono Ref')}{' '}
                <Typography display="inline" color="error">
                  *
                </Typography>
              </Typography>
              <TextField
                id="mi_gso_chrono_ref"
                size="small"
                variant="outlined"
                sx={{ width: '200px' }}
                value={chronoRef}
                onChange={(e) => setChronoRef(e.target.value)}
                error={!!referencesError && !chronoRef.trim()}
                required
              />
            </Box>
            {referencesError && (
              <Typography color="error" variant="body2">
                {referencesError}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end', pl: 3, pr: 3, pt: 1 }}>
          <Button onClick={handleReferencesModalClose} variant="text">
            {t('Cancel')}
          </Button>
          {/* {!isExtractLoading && (
            <Button
              onClick={downloadExtractAndRedirectWithReferences}
              variant="contained"
              disabled={!specRef.trim() || !chronoRef.trim()}
            >
              {t('Extract')}
            </Button>
          )} */}
          <LoadingButton
            loading={!!isExtractLoading}
            onClick={downloadExtractAndRedirectWithReferences}
            variant="contained"
            disabled={!specRef.trim() || !chronoRef.trim()}
          >
            {t('Extract')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default SummaryDataGrid;
